import { BaseButton } from '@/src/components/buttons/BaseButton'
import { useSessionStore } from '@/src/hooks/use_session_store'
import { appColors } from '@/src/theme/foundations/colors'
import { Box, Button, List, ListItem, Popover, PopoverBody, PopoverContent, PopoverTrigger, Text, useDisclosure } from '@chakra-ui/react'
import { FlexCol, FlexRow } from '@components'
import { type FC, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
////
export interface NavbarItemProps {
  id: string
  isCollapsed?: boolean
  icon?: React.ReactNode
  label: string
  comingSoonLabel?: string
  selected: boolean
  href: string
  disabled?: boolean
  showUpselling?: boolean
  actionIcon?: any
  action?: any
  subElements?: { id: string; label: string; href: string }[]
  handleCloseMobile?: () => void
  handleClick?: () => void
}

export const NavbarItem: FC<NavbarItemProps> = ({
  id,
  isCollapsed,
  icon: Icon,
  actionIcon,
  action,
  label,
  comingSoonLabel,
  selected,
  href,
  subElements,
  handleCloseMobile,
  handleClick,
  disabled,
  showUpselling,
}) => {
  const navigate = useNavigate()
  const bookingModal = useDisclosure()
  const { isOpen: isPopoverOpen, onOpen: openPopover, onClose: closePopover } = useDisclosure()
  const session = useSessionStore()
  const location = useLocation()

  const textBaseColor = appColors.secondaryForeground

  useEffect(() => {
    closePopover()
  }, [closePopover, isCollapsed, location.pathname, location.search])

  const handleNavigate = (hrefParam?: string) => {
    const localHref = hrefParam ?? href
    if (localHref) {
      if (localHref.startsWith('/')) {
        navigate(localHref, { state: { showUpselling } })
      } else {
        const companyId = session.user?.companyId
        const userId = session.user?.id
        window.open(`${localHref}?companyId=${companyId}&userId=${userId}`, '_blank')
      }
    } else {
      bookingModal.onOpen()
    }

    handleCloseMobile?.()
    closePopover()
  }

  const isSubElementSelected = (hrefVar: string, elementIndex: number) => {
    const [path, search] = hrefVar.split('?')
    const isPathMatch = location.pathname === path
    if (!new URLSearchParams(location.search)?.get('t') && elementIndex === 0 && isPathMatch) return true

    const queryParams = new URLSearchParams(search)
    const tabValue = queryParams.get('t')
    const isTabSelected = tabValue ? location.search.includes(`t=${tabValue}`) : true
    return isPathMatch && isTabSelected
  }

  const accentColor = '#0A1D3D'
  const shouldRenderPopoverContent = isCollapsed || Boolean(subElements?.length)

  return (
    <Popover
      key={label}
      trigger='hover'
      placement='right'
      openDelay={100}
      closeDelay={100}
      isOpen={isPopoverOpen}
      onOpen={openPopover}
      onClose={closePopover}
      isLazy
      lazyBehavior='unmount'
    >
      <PopoverTrigger>
        <Box w='full'>
          <FlexRow width={'100%'} alignItems={'center'} justifyContent={'space-between'}>
            <Button
              id={id}
              width={'100%'}
              height='32px'
              justifyContent={isCollapsed ? 'center' : 'start'}
              borderWidth={0}
              fontWeight={400}
              fontSize='14px'
              color={appColors.secondaryForeground}
              _hover={{ backgroundColor: appColors.accent, color: appColors.foreground }}
              _selected={{ color: appColors.foreground, backgroundColor: appColors.accent }}
              _focus={{ borderWidth: 0 }}
              borderRadius='6px'
              position='relative'
              aria-selected={selected ? 'true' : 'false'}
              style={{ padding: `6px ${isCollapsed ? '8px' : '12px'}` }}
              gap={3}
              alignItems='center'
              isDisabled={Boolean(comingSoonLabel) || disabled}
              _disabled={{ opacity: 1 }}
              onClick={() => {
                const handler = handleClick ?? handleNavigate
                if (!disabled) {
                  handler()
                  closePopover()
                }
              }}
            >
              {/* {selected && (
                <Box position='absolute' backgroundColor={accentColor} borderRadius='0px 6px 6px 0px' left={0} width='5px' height='100%' />
              )} */}
              {Icon && <Box color={selected ? accentColor : textBaseColor}>{Icon}</Box>}
              {!isCollapsed && (
                <FlexCol alignItems='flex-start'>
                  <Text color={disabled ? 'gray.300' : undefined}>{label}</Text>

                  {comingSoonLabel && (
                    <Text
                      marginLeft={label === 'Energiebeschaffung' ? '-8px' : 0}
                      bgColor='gray.400'
                      color='white'
                      display='block'
                      fontSize='10px'
                      fontStyle='italic'
                      padding='1px 4px'
                      borderRadius='4px'
                      marginTop='4px'
                    >
                      {comingSoonLabel}
                    </Text>
                  )}
                </FlexCol>
              )}
            </Button>
            {actionIcon && (
              <Box>
                <BaseButton
                  variant='tertiary'
                  onClick={() => {
                    action()
                  }}
                  ml='2px'
                  _hover={{ backgroundColor: 'accent' }}
                >
                  {actionIcon}
                </BaseButton>
              </Box>
            )}
          </FlexRow>
        </Box>
      </PopoverTrigger>

      {shouldRenderPopoverContent && (
        <PopoverContent h='auto' w='auto' _focus={{ border: 'none' }} shadow='md'>
          <PopoverBody h='100%' zIndex={99999} borderRadius='6px' backgroundColor={appColors.muted} px='8px' py='8px'>
            <FlexCol gap='10px' bgColor='transparent'>
              {isCollapsed && (
                <Text color={accentColor} fontSize={'16px'} fontWeight={500} lineHeight={'24px'} letterSpacing={'0.21px'}>
                  {label}
                </Text>
              )}
              {subElements?.length && (
                <List spacing={'1px'} bgColor={appColors.muted} zIndex={99999}>
                  {subElements.map((element, index) => {
                    const isSubElSelected = isSubElementSelected(element.href, index)
                    return (
                      <ListItem
                        id={element.id}
                        key={element.id}
                        margin='0px'
                        height='32px'
                        minW='170px'
                        fontSize={'14px'}
                        padding='6px 12px'
                        fontWeight={400}
                        lineHeight={'21px'}
                        letterSpacing={'0.21px'}
                        aria-selected={isSubElSelected ? 'true' : 'false'}
                        color={appColors.secondaryForeground}
                        borderRadius='6px'
                        _selected={{ color: appColors.foreground, backgroundColor: appColors.accent }}
                        _hover={{ color: appColors.foreground, backgroundColor: appColors.accent }}
                        cursor='pointer'
                        onClick={() => {
                          handleNavigate(element.href)
                        }}
                        whiteSpace='nowrap'
                      >
                        {element.label}
                      </ListItem>
                    )
                  })}
                </List>
              )}
            </FlexCol>
          </PopoverBody>
        </PopoverContent>
      )}
    </Popover>
  )
}
